import { useTranslation } from 'react-i18next';
import { EditableSection } from './EditableSection.js';
import { DesignLinksEditor } from './DesignLinksEditor.js';
import type { SpecDesignLink, SpecDesignLinks } from '../../../lib/api/spec-requirements.js';

export interface DesignLinksSectionProps {
  /** 今登録されているデザイン成果物。無い案件では null */
  designLinks: SpecDesignLinks | null;
  /** この節を開いているか */
  editing: boolean;
  /** 直前の保存で残った注意（上限に近づいた） */
  warnings: string[];
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 編集を開く */
  onEdit: () => void;
  /** 保存する */
  onSave: (links: SpecDesignLink[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/**
 * デザイン成果物の節。登録されていれば**名前をリンクテキストにして**並べ、無ければ「まだありません」と出す。
 *
 * **リンクにするのが本題。** ここが持っているのは在り処だけなので、URL を文字として見せても
 * 人は結局コピーして貼ることになる。名前をそのままリンクにして、1 クリックで Figma へ行ける形にする。
 *
 * **`rel="noreferrer"` を付ける。** 登録された URL は外部サイト（Figma など）で、遷移先に
 * どの画面から来たかを渡す理由が無い。`target="_blank"` と併せて開き元の窓を触らせない。
 *
 * **レビュー操作は置かない。** デザイン成果物はレビュー対象ではないので、ほかの節と違い `actions` を
 * 受け取らない（受け取れる形にすると、呼び出し側がレビュー操作を渡せてしまう）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件のデザイン成果物（名前と URL の組）を名前のリンクとして読み、編集して保存する節
 * @graph-connects DesignLinksEditor [calls] デザイン成果物の編集
 */
export function DesignLinksSection({
  designLinks,
  editing,
  warnings,
  saving,
  error,
  onEdit,
  onSave,
  onCancel,
}: DesignLinksSectionProps) {
  const { t } = useTranslation();
  const links = designLinks?.links ?? [];

  return (
    <EditableSection
      heading={t('spec.designLinks.heading')}
      action={links.length === 0 ? t('spec.designLinks.write') : t('spec.designLinks.edit')}
      editing={editing}
      warnings={warnings}
      onEdit={onEdit}
    >
      {editing ? (
        <DesignLinksEditor
          designLinks={designLinks}
          saving={saving}
          error={error}
          warnings={warnings}
          onSave={onSave}
          onCancel={onCancel}
        />
      ) : links.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t('spec.designLinks.empty')}</p>
      ) : (
        <>
          {/* 訳が無い・古いときは原文が返る。読んでいるものが原文だと分かるように断る */}
          {designLinks?.untranslated === true ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.designLinks.untranslated')}
            </p>
          ) : null}{' '}
          <ul className="mt-3 space-y-1 text-sm list-disc pl-5">
            {links.map((link) => (
              <li key={link.url}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--color-accent-primary)] underline"
                >
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </EditableSection>
  );
}
